import React, { createRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import toast from 'react-hot-toast';
import { classNames, clone, Constants, formatMiles, round } from '../../../utils';
import { CheckCircleYellowIcon, RemoveIcon } from '../../../assets/icons';
import { Vector4 } from '../../../assets/img';
import { GeneralService, PurchasesService } from '../../../services';
import { HealthStatement } from './current-dsr';
import { DocUpload } from './current-doc-upload';

const PRICE_PERIOD = Constants.PAYMENT.PERIOD;

class CurrentRequest extends React.Component {

  state = {
    isLoading: false,
    visibleDSR: false,
    visibleDocUpload: false,
    isOnlyForOwnerProducts: false,
    beneficiariesProducts: [],
    questions: [],
    identity_token: this.props.identity_token || null
  }

  healthStatementModalRef = createRef();
  docUploadModalRef = createRef();
  confirmModalRef = createRef();
  cartButtonRef = createRef();
  goBack = createRef();

  componentDidMount() {
    this.cartButtonRef.current = document.getElementById('header-cart');
    this.cartButtonRef.current.classList.add('hidden');
    this.goBack.current = document.getElementById('header-goback');
    this.goBack.current.classList.remove('hidden');

    if (!this.props.request) this.props.history.replace('/');

    // Verifica si todos los productos son sólo para el titular (independientemente de la cantidad)
    // Si es true, no necesita mostrar DSR en el siguiente paso
    this.setState({
      isOnlyForOwnerProducts: this.props.cart.every(x => !!x.is_only_for_owner),
    });

    this.getQuestions();
    this.fillForm();
  }

  componentWillUnmount() {
    this.cartButtonRef.current.classList.remove('hidden');
    this.goBack.current.classList.add('hidden');
  }

  fillForm = async () => {
    let someWithoutDSR = false;

    // Pregunta si algún producto solicita la DSR
    const someProductRequireDSR = this.props.cart.some(x => !!x.apply_dsr);
    if (someProductRequireDSR) {
      // Verifica si todos beneficiarios tienen la DSR
      someWithoutDSR = this.props.request?.beneficiaries?.some(b => {
        const noQuestions = !b?.questions?.length;
        const noAnsweredQuestion = b?.questions?.some(q => !q.answer_id);
        return noQuestions || noAnsweredQuestion;
      });
    }

    const currentProducts = this.props.request?.currentProducts || [];

    const altPriceProduct = [];
    const orphanBeneficiaries = [];

    const getAgeBySelectedPrice = (product) => {
      let minAge = 0;
      let maxAge = 0;

      if (product.is_deductible_based) {
        // Establece una edad fuera de rango para permitir buscar la edad correspondiente en el siguiente paso (getAltAgeBySelectedPrice)
        minAge = 1000;
        maxAge = 1000;

      } else {
        const selectedPrice = product?.prices?.find(x => x.id === product.selectedPriceId);
        minAge = +selectedPrice?.insurability_age?.split('-')[0];
        maxAge = +selectedPrice?.insurability_age?.split('-')[1];
      }

      return { minAge, maxAge };
    };

    const getAltAgeBySelectedPrice = (product, age) => {
      if (product.is_deductible_based) {
        // Busca el precio mínimo de la columna seleccionada
        const deductible = product?.deductibles?.find(d => d.id === product.selectedDeductible?.deductible_id);
        // Busca los precios habilitados, con el precio positivo, que sea de la misma columna seleccionada y esté en el rango de edad
        return deductible?.premiums?.find(p => {
          const isSameColumn = p.col_order === product.selectedDeductible?.col_order;
          const hasValidValue = p.price_monthly > 0 || p.price_annual > 0;
          const _minAge = +p?.insurability_age?.split('-')[0];
          const _maxAge = +p?.insurability_age?.split('-')[1];
          const isOldEnough = _minAge <= age && age <= _maxAge;
          return isSameColumn && !p.is_disabled && hasValidValue && isOldEnough;
        });

      } else {
        return product?.prices.find(price => {
          const _minAge = +price?.insurability_age?.split('-')[0];
          const _maxAge = +price?.insurability_age?.split('-')[1];
          // Si no tiene edad de asegurabilidad (0-0) entonces aplica para cualquier beneficiario
          if (!_minAge && !_maxAge) return true;
          const _isOldEnough = _minAge <= age && age <= _maxAge;
          return _isOldEnough;
        });
      }
    };

    const validateAge = (prod, benef) => {
      const isOwner = benef.id === this.props.user.id;
      // Verifica si el precio/plan seleccionado aplica para la edad del beneficiario
      if (isOwner) {
        if (this.props.user.birthdate) {
          benef.birthdate = this.props.user.birthdate;
        }
      }
      const age = moment().diff(moment(benef.birthdate || moment().format('YYYY-MM-DD')), 'years');
      const { minAge, maxAge } = getAgeBySelectedPrice(prod);

      // Si no tiene edad de asegurabilidad (0-0) entonces aplica para cualquier beneficiario
      if (!minAge && !maxAge) return true;
      const isOldEnough = minAge <= age && age <= maxAge;

      // Verifica si la edad de asegurabilidad del producto NO aplica para el beneficiario
      if (!isOldEnough || (isOwner && !this.props.user.birthdate)) {
        // Verifica las edades disponibles del producto para aplicar la que se adecue al beneficiario
        const altPrice = getAltAgeBySelectedPrice(prod, age);

        if (altPrice) {
          // Verifica si en los precios alternativos ya existe el producto y precio seleccionado
          const altPriceIdx = prod.is_deductible_based
            ? altPriceProduct.findIndex(altPri => altPri.productId === prod.id && altPri.selectedDeductibleId === altPrice.deductible_id && altPri.selectedDeductiblePremiumId === altPrice.id)
            : altPriceProduct.findIndex(altPri => altPri.productId === prod.id && altPri.selectedPriceId === altPrice.id);

          if (altPriceIdx >= 0) {
            // Si existe, solo se agrega el beneficiario
            altPriceProduct[altPriceIdx].beneficiaries.push({ ...benef });

          } else {
              const itemID = prod.is_deductible_based
                ? { selectedDeductibleId: altPrice.deductible_id, selectedDeductiblePremiumId: altPrice.id }
                : { selectedPriceId: altPrice.id };

              // Si no existe, se inserta el producto
              altPriceProduct.push({
                productId: prod.id,
                product: clone(prod),
                beneficiaries: [{ ...benef }],
                ...itemID,
              });
          }
        } else {
          if (!isOwner || this.props.user.birthdate != null) {
            orphanBeneficiaries.push(`${benef.fullName}: ${prod.name}`);
          }
        }
      }

      return isOldEnough;
    };

    let products = this.props.cart.map(prod => {
      let beneficiaries = clone(this.props.request?.beneficiaries || []);

      beneficiaries = beneficiaries.map(benef => {
        const prevProduct = this.props.request?.products?.find(x => x.productId === prod.id);
        const prevBeneficiary = prevProduct?.beneficiaries?.find(x => x.id === benef.id);
        const prevPricePeriod = prevBeneficiary?.pricePeriod || null;
        const prevRequiredDocs = prevBeneficiary?.required_docs || prod?.required_docs;

        benef.pricePeriod = prevPricePeriod;
        benef.required_docs = prevRequiredDocs;

        return benef;
      });

      // Obtiene los beneficiarios en base a los productos previamente adquiridos para exluirlos (no debe repetirse el producto por beneficiario)
      const userIds = currentProducts.filter(x => x.product_id === prod.id).map(x => x.user_id);

      // TODO: Si el producto es por deducibles hay que establecer un producto por cada beneficiario y validar la edad de cada uno

      // Excluir los beneficiarios de los productos que solo aplican para titulares
      if (prod.is_only_for_owner) {
        // Si se seleccionó el titular, será el único beneficiario del producto, si no, quedará vacío para porterior eliminación del listado
        const isOwnerSelected = beneficiaries.find(x => x.id === this.props.user.id);
        // Verifica si posee el producto
        const hasProduct = userIds.includes(this.props.user.id);
        if (isOwnerSelected && !hasProduct) {
          if (validateAge(prod, isOwnerSelected)) beneficiaries = [isOwnerSelected];
        }
      } else {
        beneficiaries = beneficiaries.filter(beneficiary => {
          // Establece los beneficiarios seleccionados que no tengan el producto
          const exists = userIds.includes(beneficiary.id);
          if (exists) return false;
          const benef = clone({ ...beneficiary, required_docs: prod.required_docs });
          return validateAge(prod, benef);
        });
      }

      return {
        productId: prod.id,
        product: clone(prod),
        selectedPriceId: prod.selectedPriceId,
        beneficiaries,
      };
    })
      // Elimina los productos del carrito que no tenga beneficiarios debido al paso anterior
      .filter(x => !!x.beneficiaries.length);

    // TODO: Agregar los productos que faltan y que correspondan a la edad
    for (let i = 0; i < altPriceProduct.length; i++) {
      let exists = false;
      altPriceProduct[i].beneficiaries = altPriceProduct[i].beneficiaries.map(benef => {
        const prevProducts = this.props.request?.products?.filter(x => x.productId === altPriceProduct[i].productId);
        if (prevProducts) {
          for (let j = 0; j < prevProducts.length; j++) {
            const prevProduct = prevProducts[j];
            const prevBeneficiary = prevProduct?.beneficiaries?.find(x => x.id === benef.id);
            const prevPricePeriod = prevBeneficiary?.pricePeriod || null;

            if (!benef.pricePeriod) {
              benef.pricePeriod = prevPricePeriod;
            }
          }
        }

        exists = products.find(item => item.productId == altPriceProduct[i].productId && altPriceProduct[i].beneficiaries.map(i => i.id).some(i => item.beneficiaries.map(i => i.id).includes(i))) != null;

        return benef;
      });

      if (!exists) {
        products.push(altPriceProduct[i]);
      }
    }

    let isPricePeriodSelected = false;

    isPricePeriodSelected = products.every(item => {
      return item.beneficiaries.every(benef => !!benef.pricePeriod);
    });

    await this.props.dispatch({
      type: 'SET_REQUEST',
      payload: {
        ...this.props.request,
        isDSRAnswered: !someWithoutDSR,       // Valida si ya fue respondida la Declaración de Salud Resumida
        isPricePeriodSelected,
        products,
      },
    });

    if (orphanBeneficiaries.length) {
      let message = 'Los siguientes beneficiarios no cumplen con la edad de asegurabilidad:\n\n';
      orphanBeneficiaries.forEach((item, index) => {
        message += `${index + 1}. ${item} \n`;
      });
      toast.error(message);
    }

    return orphanBeneficiaries;
  }

  getQuestions = async () => {
    try {
      const questions = await GeneralService.getQuestions();
      this.setState({ questions });

    } catch (error) {
      console.log(error);
    }
  }

  selectPricePeriod = (prodIdx, benefIdx, value) => {
    const products = clone(this.props.request.products);
    // Establece el valor individualmente
    // products[prodIdx].beneficiaries[benefIdx].pricePeriod = value;

    // Establece el valor a todos los beneficiarios del producto
    for (let index = 0; index < products[prodIdx].beneficiaries.length; index++) {
      products[prodIdx].beneficiaries[index].pricePeriod = value;
    }

    let isPricePeriodSelected = false;

    // Verifica si todos los productos/beneficiarios tienen seleccionado el periodo de cobro
    isPricePeriodSelected = products.every(item => {
      return item.beneficiaries.every(benef => !!benef.pricePeriod);
    });

    this.props.dispatch({
      type: 'SET_REQUEST',
      payload: {
        ...this.props.request,
        isPricePeriodSelected,
        products,
      },
    });
  }

  removeBeneficiary = (prodIdx, benefIdx) => {
    let products = clone(this.props.request.products);
    const benefID = products[prodIdx].beneficiaries[benefIdx].id;
    products[prodIdx].beneficiaries = products[prodIdx].beneficiaries.filter((_, i) => i !== benefIdx);

    // Si no hay más beneficiarios en el producto, elimino el producto de la solicitud
    if (!products[prodIdx].beneficiaries.length) {
      products = products.filter((_, idx) => idx !== prodIdx);
    }

    // Si el beneficiario no se encuentra en otros productos, elimino el beneficiario de la solicitud
    const benefIsInAnotherProd = products.some(p => p.beneficiaries.some(b => b.id === benefID));
    let beneficiaries = clone(this.props.request.beneficiaries);
    if (!benefIsInAnotherProd) {
      beneficiaries = beneficiaries.filter(b => b.id !== benefID);
    }

    let isPricePeriodSelected = this.props.request.isPricePeriodSelected;

    if (products.length) {
      // Verifica si todos los productos/beneficiarios tienen seleccionado el periodo de cobro
      isPricePeriodSelected = products.every(item => {
        return item.beneficiaries.every(benef => !!benef.pricePeriod);
      });

    } else {
      // Si elimina todos los productos no le permite continuar
      isPricePeriodSelected = false;
    }

    this.setState({
      isOnlyForOwnerProducts: products.every(x => !!x.product?.is_only_for_owner),
    });

    this.props.dispatch({
      type: 'SET_REQUEST',
      payload: {
        ...this.props.request,
        isPricePeriodSelected,
        beneficiaries,
        products,
      },
    });
  }

  getBeneficiariesNames = () => {
    return this.props.request?.beneficiaries?.map(x => x.fullName)?.join(', ');
  }

  getTotals = () => {
    let subtotal = 0;
    let iva = 0;
    let igtf = 0;
    let total = 0;

    const getPrice = (item, pricePeriod) => {
      let price = null;

      if (item.product.is_deductible_based) {
        const deductibles = item?.product?.deductibles || [];
        const deductible = deductibles.find(d => d.id === item?.selectedDeductibleId);
        price = deductible?.premiums?.find(p => p.id === item?.selectedDeductiblePremiumId);

      } else {
        const prices = item?.product?.prices || [];
        price = prices?.find(x => x.id === item?.selectedPriceId);
      }

      switch (pricePeriod) {
        case PRICE_PERIOD.ANNUAL: return price?.price_annual || 0;
        case PRICE_PERIOD.MONTHLY: return price?.price_monthly || 0;
        default: return 0;
      }
    };

    for (const item of this.props.request?.products || []) {
      for (const benef of item.beneficiaries) {
        let price = getPrice(item, benef?.pricePeriod);
        subtotal += parseFloat(price);
        iva += parseFloat(price);
      }
    }

    total = subtotal;

    return { subtotal, iva, igtf, total };
  }

  toggleHealthStatement = () => {
    this.setState({ visibleDSR: !this.state.visibleDSR });
    if (this.state.visibleDSR) {
      this.healthStatementModalRef.current?.close();
    } else {
      this.healthStatementModalRef.current?.showModal();
    }
  }

  toggleDocUpload = () => {
    this.setState({ visibleDocUpload: !this.state.visibleDocUpload });
    if (this.state.visibleDocUpload) {
      this.docUploadModalRef.current?.close();
    } else {
      this.docUploadModalRef.current?.showModal();
    }
  }

  closeConfirmModal = () => {
    this.confirmModalRef.current?.close();
    this.props.dispatch({ type: 'SET_REQUEST', payload: {} });
    this.props.dispatch({ type: 'SET_CART', payload: [] });
    this.props.history.push('/historial/pagos');
  }

  isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    };

    if (!this.props.request?.products?.length) {
      return onError('Debe seleccionar al menos un producto');
    }

    if (!this.props.request?.beneficiaries?.length) {
      return onError('Debe seleccionar al menos un beneficiario');
    }

    // Pregunta si algún producto solicita la DRS
    const someProductRequireDSR = this.props.cart.some(x => !!x.apply_dsr);
    let someWithoutDSR = false;
    if (someProductRequireDSR) {
      // Verifica si todos beneficiarios tienen la DSR
      someWithoutDSR = this.props.request.beneficiaries.some(b => {
        const noQuestions = !b?.questions?.length;
        const noAnsweredQuestion = b?.questions?.some(q => !q.answer_id);
        return noQuestions || noAnsweredQuestion;
      });
    }

    if (someWithoutDSR) {
      this.toggleHealthStatement();
      return onError('Debe completar la Declaración de Salud Resumida para continuar');
    }

    return true;
  }

  submit = async () => {
    if (!this.props.request.isPricePeriodSelected) {
      return toast.error('Debe seleccionar el periodo de cobro para cada beneficiario');
    }

    if (!this.props.request.isDSRAnswered) {
      return this.toggleHealthStatement();
    }

    const someProdWithDocsNotUploaded = this.props.request.products?.some(x => {
      return x.beneficiaries.some(b => {
        return b.required_docs?.some(d => !d?.is_optional && !d?.file) || false;
      }) || false;
    });

    if (!this.props.request.isDocUploaded || someProdWithDocsNotUploaded) {
      return this.toggleDocUpload();
    }

    const orphanBeneficiaries = await this.fillForm();
    if (orphanBeneficiaries.length > 0) {
      return;
    }

    if (!this.isValidForm()) return;

    const { request } = this.props;

    const totals = this.getTotals();

    const purchase = {
      conversion: 1, // Tasa de Cambio por defecto (en caso de proceder con algún método de pago nacional se modifica al enviar el form)
      subtotal: round(totals.subtotal),
      iva: round(totals.iva),
      total: round(totals.total),
      paymentType: Constants.PAYMENT.TYPE.PURCHASE,
      toAnalize: Number(request.toAnalize || false),
      products: [],
      questions: [],
      hasFile: false,
    };

    request.products.forEach(cartItem => {
      cartItem.beneficiaries.forEach(benef => {
        // Verifica si el producto tiene documentos requeridos
        if (benef?.required_docs?.length) {
          purchase.hasFile = true;
          // Agrega los documentos requeridos a la raíz del objeto
          benef?.required_docs?.forEach(x => {
            purchase[`required_docs_${x.id}_${benef.id}`] = x.file;
          });
        }

        let formVehicle = undefined;
        for (let i = 0; i < benef?.required_docs?.length; i++) {
          if (benef?.required_docs[i].formVehicle) {
            formVehicle = benef?.required_docs[i].formVehicle;
          }
        }

        purchase.products.push({
          productId: cartItem.product.id,
          userId: benef.id,
          deductibleId: cartItem?.selectedDeductibleId,
          deductiblePremiumId: cartItem?.selectedDeductiblePremiumId,
          priceId: cartItem?.selectedPriceId,
          pricePeriodId: benef.pricePeriod,
          formVehicle,
          identity_token: this.state.identity_token
        });
      });
    });

    request.beneficiaries.forEach(b => {
      b?.questions?.forEach(q => purchase?.questions?.push({
        userId: b.id,
        questionId: q.id,
        answerId: q.answer_id,
      }));
    });

    const hasUserIds = request?.products?.every(p => p?.beneficiaries?.every(b => !!b.id));
    if (!hasUserIds) {
      return toast.error('No se pudo identificar alguno de los beneficiarios. Por favor reinicia el proceso de compra.');
    }

    if (request.toAnalize) {
      this.setState({ isLoading: true });

      if (purchase.hasFile) {
        purchase.products = JSON.stringify(purchase.products);
        purchase.questions = JSON.stringify(purchase.questions);
      }

      try {
        await PurchasesService.createWithoutPayment(purchase);
        this.confirmModalRef.current?.showModal();

      } catch (error) {
        console.log(error);
      }

      this.setState({ isLoading: false });
      return;
    }

    this.props.dispatch({ type: 'SET_REQUEST', payload: { ...request, purchase } });
    this.props.history.push('/pagar');
  }

  getSelectedPrice = (itemProd) => {
    if (itemProd?.product?.is_deductible_based) {
      const deductible = itemProd.product.deductibles?.find(d => d.id === itemProd.selectedDeductibleId);
      const premium = deductible?.premiums.find(p => p.id === itemProd.selectedDeductiblePremiumId);
      return { ...premium, name: deductible.name };

    } else {
      return itemProd?.product?.prices?.find(x => x.id === itemProd.selectedPriceId);
    }
  }

  render() {
    const { isLoading } = this.state
    const { request } = this.props;
    const totals = this.getTotals();
    const isPaymentEnabled = !!request?.isPricePeriodSelected;
    const isDSREnabled = !!request?.isDSRAnswered;
    const isToAnalize = !!request?.toAnalize;

    return (
      <>
        <dialog ref={this.healthStatementModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
          {this.state.visibleDSR && (
            <HealthStatement
              onClose={this.toggleHealthStatement}
              questions={this.state.questions}
            />
          )}
        </dialog>

        <dialog ref={this.docUploadModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent" id="doc-upload-modal">
          {this.state.visibleDocUpload && (
            <DocUpload
              setIdentityToken={ (identity_token) => {
                this.setState({
                  identity_token
                });
                this.props.dispatch({
                  type: 'SET_IDENTITY_TOKEN',
                  payload: identity_token
                })
              } }
              identityToken={ this.state.identity_token }
              onClose={this.toggleDocUpload}
              onSubmit={this.submit} />
          )}
        </dialog>

        <dialog
          ref={this.confirmModalRef}
          className="fixed inset-0 z-50 overflow-y-auto bg-transparent"
          onClose={this.closeConfirmModal}
        >
          <div
            className="w-[20rem] bg-white rounded-xl overflow-hidden p-4 flex flex-col items-center"
            onClick={this.closeConfirmModal}
          >
            <img src={Vector4} className="w-32 h-32 object-contain" alt="" />
            <p className="text-center text-sm font-semibold">
              Su solicitud se encuentra en análisis. Próximamente será contactado por nosotros
            </p>
          </div>
        </dialog>

        <div className="bg-black mt-5 lg:-mt-4 -mx-4 p-4 pt-2">
          <p className="text-white font-semibold">Beneficiarios</p>
          <p className="text-white">{ this.getBeneficiariesNames() }</p>
        </div>

        <div className="max-w-[600px] mx-auto pt-4">
          <div className="w-full">
            {request?.products?.filter(x => x.beneficiaries?.length > 0)?.map((itemProd, prodIdx) => {
              const selectedPrice = this.getSelectedPrice(itemProd);

              return (
                <div key={prodIdx}>
                  <div>
                    <p className="font-semibold text-lg text-black pb-1">
                      { itemProd.product.name }
                    </p>
                  </div>

                  {itemProd.beneficiaries?.map((beneficiary, benefIdx) => {
                    const fees = itemProd?.product?.fees;
                    const hasAditionalPrices = !!itemProd?.product?.prices?.length || !!itemProd?.product?.is_deductible_based;
                    const hasPriceDetails = itemProd?.product?.is_deductible_based
                      ? !!itemProd?.selectedDeductiblePremiumId
                      : !!itemProd?.selectedPriceId && itemProd?.selectedPriceId > 0;

                    return (
                      <div key={benefIdx} className="bg-white rounded-md mb-4 shadow-md">
                        <div className="flex justify-between items-center px-2 py-1">
                          <p className="font-semibold text-gray2">
                            { beneficiary.fullName }
                          </p>
                          <button onClick={() => this.removeBeneficiary(prodIdx, benefIdx)}>
                            <img src={RemoveIcon} className="w-6 h-6 object-contain py-1" alt="" />
                          </button>
                        </div>

                        {hasPriceDetails && (
                          <div className="flex justify-between items-center px-2 py-1 border-t border-t-gray6">
                            <p className="font-semibold text-sm text-darkBlue">
                              <span>{ selectedPrice?.name }</span>
                              {(!!selectedPrice?.insurability_age && selectedPrice?.insurability_age !== '0-0') && (
                                <span> - { selectedPrice?.insurability_age } años</span>
                              )}
                              {!!selectedPrice?.deductible && (
                                <span> - Deducible { selectedPrice?.deductible }</span>
                              )}
                            </p>
                          </div>
                        )}

                        {(hasAditionalPrices && selectedPrice?.price_monthly > 0) && (
                          <PricePeriod
                            label="Prima Mensual"
                            amount={selectedPrice?.price_monthly}
                            fees={fees}
                            isSelected={beneficiary.pricePeriod === PRICE_PERIOD.MONTHLY}
                            onClick={() => this.selectPricePeriod(prodIdx, benefIdx, PRICE_PERIOD.MONTHLY)}
                          />
                        )}

                        {(hasAditionalPrices && selectedPrice?.price_annual > 0) && (
                          <PricePeriod
                            label="Prima Anual"
                            amount={selectedPrice?.price_annual}
                            isSelected={beneficiary.pricePeriod === PRICE_PERIOD.ANNUAL}
                            onClick={() => this.selectPricePeriod(prodIdx, benefIdx, PRICE_PERIOD.ANNUAL)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {!isDSREnabled && (
              <p className="font-semibold text-gray3 text-center my-10">
                Para seguir con tu compra debes{'\n'}llenar "Declaración de Salud Resumida".
              </p>
            )}
          </div>

          <div className="bg-[#F2F2F2] px-5 pb-2 rounded-2xl shadow-md">
            <div className="my-2">
              {/* <View className="flex justify-between items-center my-2 py-1">
                <span className="font-semibold text-gray4">Sub-Total</span>
                <span className="font-bold text-xl text-darkBlue">{ formatMiles(totals.subtotal) }</span>
              </View> */}
              {/* <View className="flex justify-between items-center my-2 py-1">
                <span className="font-semibold text-gray4">Impuesto</span>
                <span className="font-bold text-xl text-darkBlue">{ formatMiles(totals.iva) }</span>
              </View> */}
              {/* <View className="flex justify-between items-center my-2 py-1">
                <span className="font-semibold text-gray4">IGTF</span>
                <span className="font-bold text-xl text-darkBlue">{ formatMiles(totals.igtf) }</span>
              </View> */}
              <div className="flex justify-between items-center my-2 py-1">
                <span className="font-semibold text-gray4">Total</span>
                <span className="font-bold text-xl text-darkBlue">{ formatMiles(totals.total) }</span>
              </div>
            </div>
            <div className="flex justify-center">
              {isDSREnabled ? (
                <button
                  className="rounded-lg w-60 text-center h-10 font-bold my-2 disabled:bg-gray6 disabled:text-gray3 bg-yellow text-black"
                  onClick={this.submit}
                  disabled={isLoading}
                >
                  <span>{ isToAnalize ? 'Guardar' : 'Pagar' }</span>
                </button>
              ) : (
                <button
                  className={classNames(
                    'rounded-lg w-60 text-center h-10 font-bold my-2 disabled:bg-gray6 disabled:text-gray3',
                    isPaymentEnabled ? 'bg-yellow text-black' : 'text-gray bg-gray6'
                  )}
                  onClick={this.submit}
                  disabled={isLoading}
                >
                  <span>Continuar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

const PricePeriod = ({ label, amount, fees, isSelected, onClick }) => (
  <button
    className="w-full flex justify-between items-center px-2 py-1 border-t border-t-gray6"
    onClick={onClick}
  >
    <div className="flex items-center gap-x-2">
      {isSelected ? (
        <img src={CheckCircleYellowIcon} className="w-5 h-5 mr-2 object-contain" alt="" />
      ) : (
        <div className="w-5 h-5 mr-2 aspect-square rounded-full border bg-gray5 border-gray6" />
      )}
      <p className="flex-1">
        <span className="font-semibold text-sm text-darkBlue">
          { label }
        </span>
        {!!fees && (
          <span className="ml-2 font-bold text-xs text-darkBlue">
            ({ fees } {fees > 1 ? 'Cuotas' : 'Cuota'})
          </span>
        )}
      </p>
    </div>
    <span className="font-bold text-darkBlue text-sm">
      { formatMiles(amount) }
    </span>
  </button>
);

export default connect(state => ({
  request: state.request,
  cart: state.cart,
  user: state.user,
  identity_token: state.identity_token
}))(CurrentRequest);
